import { useState, useRef, useEffect } from "react";

/**
 * Custom hook for voice input using Web Speech API
 * Converts speech to text automatically
 */
export const useVoiceInput = (onTranscript) => {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Check if browser supports Web Speech API
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setIsSupported(true);
      recognitionRef.current = new SpeechRecognition();
      const recognition = recognitionRef.current;

      // Configure recognition settings
      recognition.continuous = false; // Stop after user stops speaking
      recognition.interimResults = true; // Show interim results
      recognition.lang = "en-US"; // Default language

      // Handle results
      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";

        // Process all results
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          } else {
            interimTranscript += transcript;
          }
        }

        // If we have final results, use those (they're more accurate)
        // Otherwise, show interim results for real-time feedback
        if (finalTranscript && onTranscript) {
          onTranscript(finalTranscript.trim(), true);
        } else if (interimTranscript && onTranscript) {
          onTranscript(interimTranscript, false);
        }
      };

      // Handle errors
      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);

        let errorMessage = "Speech recognition error occurred";
        switch (event.error) {
          case "no-speech":
            errorMessage = "No speech detected. Please try again.";
            break;
          case "audio-capture":
            errorMessage = "Microphone not found or not accessible.";
            break;
          case "not-allowed":
            errorMessage = "Microphone permission denied. Please allow microphone access.";
            break;
          case "network":
            errorMessage = "Network error. Please check your connection.";
            break;
          case "aborted":
            // User stopped manually, don't show error
            return;
          default:
            errorMessage = `Speech recognition error: ${event.error}`;
        }
        setError(errorMessage);
      };

      // Handle end of recognition
      recognition.onend = () => {
        setIsListening(false);
      };
    } else {
      setIsSupported(false);
      setError("Speech recognition is not supported in your browser.");
    }

    // Cleanup
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [onTranscript]);

  const startListening = () => {
    if (!isSupported) {
      setError("Speech recognition is not supported in your browser.");
      return;
    }

    if (recognitionRef.current && !isListening) {
      try {
        setError(null);
        setIsListening(true);
        recognitionRef.current.start();
      } catch (err) {
        console.error("Error starting speech recognition:", err);
        setError("Failed to start voice recording. Please try again.");
        setIsListening(false);
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (err) {
        console.error("Error stopping speech recognition:", err);
        setIsListening(false);
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return {
    isListening,
    isSupported,
    error,
    startListening,
    stopListening,
    toggleListening,
  };
};

